import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { FeedbackHistoryObj } from '../../types/feedback'
import { api } from '../../utils/api-client'
import { queryKeys } from './keys'

// Shared so the sidebar preloader can prefetch the same query (key + fn) it
// otherwise only fires on the first menu open.
export const feedbackUnreadQueryKey = (username: string) => [
  ...queryKeys.feedback_unread,
  username,
]

export const fetchFeedbackUnreadCount = async (
  signal?: AbortSignal,
): Promise<number> => {
  const res = await api.post<ApiResponse<number>>(
    '/ajax_data.php',
    { type: 'get_feedback_unread_count' },
    { signal },
  )
  return res.data.status === 1 ? Number(res.data.info) || 0 : 0
}

export function useFeedbackUnread() {
  const username = useBoundStore(s => s.user.username)
  const queryClient = useQueryClient()
  const queryKey = feedbackUnreadQueryKey(username)
  const historyKey = [...queryKeys.feedback_history, username]

  const { data, isFetching } = useQuery<number>({
    queryKey,
    queryFn: ({ signal }) => fetchFeedbackUnreadCount(signal),
    enabled: Boolean(username),
    staleTime: 0,
    refetchOnWindowFocus: false,
  })

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<ApiResponse<string>>('/ajax_data.php', {
        type: 'set_feedback_read',
        id,
      })
      if (res.data.status !== 1) {
        throw new Error(res.data.info)
      }
    },
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey })
      await queryClient.cancelQueries({ queryKey: historyKey })
      const history = queryClient.getQueryData<FeedbackHistoryObj[]>(historyKey)
      const prevItemIsRead = history?.find(f => f.id === id)?.is_read
      const wasUnread = prevItemIsRead === 0
      if (wasUnread) {
        queryClient.setQueryData<number>(queryKey, prev =>
          Math.max(0, (prev ?? 0) - 1),
        )
      }
      queryClient.setQueryData<FeedbackHistoryObj[]>(historyKey, prev =>
        prev?.map(f => (f.id === id ? { ...f, is_read: 1 } : f)),
      )
      return { id, wasUnread, prevItemIsRead }
    },
    onError: (_err, _id, ctx) => {
      if (!ctx) return
      if (ctx.wasUnread) {
        queryClient.setQueryData<number>(queryKey, prev => (prev ?? 0) + 1)
      }
      queryClient.setQueryData<FeedbackHistoryObj[]>(historyKey, prev =>
        prev?.map(f =>
          f.id === ctx.id ? { ...f, is_read: ctx.prevItemIsRead } : f,
        ),
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: historyKey })
    },
  })

  const markSeen = (id: string) => {
    if (!username) return
    mutation.mutate(id)
  }

  return {
    count: data ?? 0,
    isLoading: isFetching,
    markSeen,
    isMarkingSeen: mutation.isPending,
  }
}
